import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { notificationAlertActions } from 'app/pages/NotificationAlert/slice';
import type { Section, SectionAction } from '../types';
import MetricsFrameworkApiService from '../apiService/metricsFrameworkApiService';
import { useMetricDrilldown } from '../context/MetricDrilldownContext';
import { useMetricsFrameworkContext } from '../context/MetricsFrameworkContext';
import { buildMetricCacheKey } from '../utils/metricCacheKey';
import { downloadCsvFile, metricTableToCsv, toCsvFileName } from '../utils/metricTableCsv';
import { metricTileCache } from '../utils/tileCache';
import {
  buildMetricsFrameworkBreadcrumbs,
  buildMetricsFrameworkPath,
  resolveMetricsFrameworkBasePath,
} from '../utils/viewConfig';

/** Handles section action links: L2, L3, exportCsv, and in-app route redirects. */
export const useSectionAction = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { openSectionL2 } = useMetricDrilldown();
  const { filters, mockApiFlag, dataRevision, dealerTitle, activeView } =
    useMetricsFrameworkContext();

  const handleSectionAction = useCallback(
    async (section: Section, action?: SectionAction) => {
      const navigation = (action ?? section.action)?.navigation;
      if (!navigation) return;

      if (navigation.type === 'L2') {
        openSectionL2(section);
        return;
      }

      if (navigation.type === 'L3') {
        const basePath = resolveMetricsFrameworkBasePath(location.pathname);
        history.push(buildMetricsFrameworkPath(basePath, navigation.tab));
        return;
      }

      if (navigation.type === 'route') {
        const basePath = resolveMetricsFrameworkBasePath(location.pathname);
        const parentCrumbs = buildMetricsFrameworkBreadcrumbs({
          dealerTitle,
          basePath,
          selectedView: activeView,
        }).map(crumb => ({ ...crumb, isActive: false }));
        history.push(navigation.path, {
          breadcrumbsList: [
            ...parentCrumbs,
            {
              label: 'Autopilot',
              link: navigation.path,
              isActive: true,
              componentLinked: 'metrics',
              filter: {},
            },
          ],
        });
        return;
      }

      if (navigation.type === 'exportCsv') {
        const metricItem = section.items.find(item => item.kind === 'metric');
        if (!metricItem || !filters?.dealerId) {
          return;
        }

        try {
          const key = buildMetricCacheKey(
            metricItem.id,
            filters,
            mockApiFlag,
            dataRevision,
            'L1',
          );
          let cached = metricTileCache.get(key);
          if (!cached) {
            cached = await MetricsFrameworkApiService.getMetric(
              metricItem.id,
              filters,
              mockApiFlag,
              'L1',
            );
            metricTileCache.set(key, cached);
          }

          const csv = metricTableToCsv(cached.data.table);
          if (!csv) {
            return;
          }

          const fileBase = toCsvFileName(cached.data.display_name || section.id);
          downloadCsvFile(csv, `${fileBase}.csv`);
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : 'Failed to export CSV';
          dispatch(
            notificationAlertActions.addNotification({
              open: true,
              severity: 'error',
              message,
            }),
          );
        }
      }
    },
    [
      dataRevision,
      dispatch,
      filters,
      history,
      location.pathname,
      mockApiFlag,
      openSectionL2,
      dealerTitle,
      activeView,
    ],
  );

  return { handleSectionAction };
};
